import { FC, useState } from 'react'
import { Box, Modal, Typography, Button, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { useIntl } from 'react-intl'
import {
  useCancelCommonRoomReservation,
  useCommonRoomsReservationsList
} from 'services/commonRooms/commonRooms.hooks'
import { useFormik } from 'formik'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'

const CancelReservation: FC<{ reservationId: number; buttonSx?: SxProps<Theme> }> = ({
  reservationId,
  buttonSx
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [showDialog, setShowDialog] = useState(false)
  const { formatMessage } = useIntl()
  const cancelReservation = useCancelCommonRoomReservation(reservationId)
  const { refetch } = useCommonRoomsReservationsList()

  const cancelReservationForm = useFormik({
    initialValues: {
      reason: ''
    },
    validationSchema: Yup.object({
      reason: Yup.string().required('Wymagany')
    }),
    onSubmit: async ({ reason }) => {
      try {
        await cancelReservation.mutateAsync(reason)
        setShowDialog(false)
        await refetch()
        enqueueSnackbar(
          formatMessage({
            id: 'commonRoomReservations.cancelReservationSuccess',
            defaultMessage: 'Rezerwacja została anulowana pomyślnie.'
          }),
          {
            variant: 'success'
          }
        )
        cancelReservationForm.resetForm()
      } catch (error) {
        enqueueSnackbar(
          formatMessage({
            id: 'commonRoomReservations.cancelReservationError',
            defaultMessage: 'Nie można anulować rezerwacji.'
          }),
          {
            variant: 'error'
          }
        )
      }
    }
  })

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        variant='contained'
        color='error'
        fullWidth={!buttonSx}
        sx={buttonSx}>
        {formatMessage({
          id: 'commonRoomReservations.cancelReservation',
          defaultMessage: 'Anuluj rezerwację'
        })}
      </Button>
      <Modal
        open={showDialog}
        onClose={() => {
          setShowDialog(false)
          cancelReservationForm.resetForm()
        }}
        aria-labelledby='reservation-modal-title'
        aria-describedby='reservation-modal-description'>
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            maxWidth: {
              xs: '90%',
              sm: 420
            },
            p: 4,
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: 2
          }}>
          <Typography variant='h5' fontWeight={600} mb={3}>
            {formatMessage({
              id: 'commonRoomReservations.cancelReservation',
              defaultMessage: 'Anuluj rezerwację'
            })}
          </Typography>
          <TextField
            name='reason'
            onChange={cancelReservationForm.handleChange}
            value={cancelReservationForm.values.reason}
            label={formatMessage({
              id: 'commonRoomReservations.cancelReservationReason',
              defaultMessage: 'Powód anulowania rezerwacji'
            })}
            error={
              cancelReservationForm.touched.reason && Boolean(cancelReservationForm.errors.reason)
            }
            helperText={cancelReservationForm.touched.reason && cancelReservationForm.errors.reason}
            fullWidth
            rows={5}
            multiline
            disabled={cancelReservationForm.isSubmitting}
          />
          <LoadingButton
            loading={cancelReservationForm.isSubmitting}
            fullWidth
            sx={{ mt: 4 }}
            variant='contained'
            color='error'
            onClick={() => cancelReservationForm.submitForm()}>
            {formatMessage({
              id: 'commonRoomReservations.cancelReservationConfirm',
              defaultMessage: 'Potwierdź anulowanie rezerwacji'
            })}
          </LoadingButton>
          <Button
            fullWidth
            sx={{ mt: 2 }}
            variant='outlined'
            color='primary'
            onClick={() => {
              setShowDialog(false)
              cancelReservationForm.resetForm()
            }}>
            {formatMessage({
              id: 'commonRoomReservations.cancelReservationAbort',
              defaultMessage: 'Anuluj'
            })}
          </Button>
        </Box>
      </Modal>
    </>
  )
}

export default CancelReservation
